import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  OnInit,
  inject
} from '@angular/core';
import { ContentLanguage, StorageKey } from '@coin/shared/core/util-enums';
import { GlobalEventsService, helpDeepLinkOpened$ } from '@coin/shared/core/util-helpers';
import { FaqItem } from '@coin/shared/core/util-models';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { filter, finalize, iif, of, switchMap, tap } from 'rxjs';
import {
  FaqService,
  FeatureFlagsService,
  HttpHelpersService,
  LoadingService,
  StorageService
} from '@coin/shared/core/data-access';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { environment } from '@coin/shared/core/util-environments';
import { HttpClient } from '@angular/common/http';
import { DownloadFileHelper } from '@coin/shared/core/util-helpers';
import { MatIcon } from '@angular/material/icon';
import { MatBadge } from '@angular/material/badge';
import { V2ButtonComponent } from '@coin/shared/core/ui-storybook';
import { LazyFaqMenuComponent } from '@coin/shared/auth/feature';

@Component({
  selector: 'cosmos-help-menu',
  templateUrl: './help-menu.component.html',
  styleUrls: ['./help-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatIcon, MatBadge, LazyFaqMenuComponent, V2ButtonComponent, TranslatePipe]
})
export class HelpMenuComponent implements OnInit {
  private readonly translateService = inject(TranslateService);
  private readonly storageService = inject(StorageService);
  private readonly faqService = inject(FaqService);
  private readonly eventsService = inject(GlobalEventsService);
  private readonly elementRef = inject(ElementRef);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly httpClient = inject(HttpClient);
  private readonly loadingService = inject(LoadingService);
  private readonly httpHelpers = inject(HttpHelpersService);
  private readonly featureFlagsService = inject(FeatureFlagsService);
  private readonly helpDeepLinkOpened$ = helpDeepLinkOpened$();

  public readonly isSeomDownloadActive = this.featureFlagsService.isActive('cosmosSeomDownload');

  public isHelpMenuVisible = false;

  public isHelpDataLoading = false;
  public languageSelected: ContentLanguage = ContentLanguage.ENGLISH;
  public helpData: { [lang in ContentLanguage]?: FaqItem[] } = {};

  public totalNewFlags = 0;

  clickout = (event: MouseEvent): void => {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      if (this.isHelpMenuVisible) {
        this.toggleMenu();
      }
    }
  };

  public async ngOnInit(): Promise<void> {
    this.openMenuOnDeepLink();
    await this.loadTranslations();
    this.eventsService
      .listen('click')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(event => this.clickout(event));
  }

  public toggleMenu(): void {
    this.isHelpMenuVisible = !this.isHelpMenuVisible;
    this.cdr.detectChanges();
  }

  private openMenuOnDeepLink(): void {
    this.helpDeepLinkOpened$
      .pipe(
        filter(() => !this.isHelpMenuVisible),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.toggleMenu());
  }

  public onOpenOtherFaqTopic(title: string): void {
    if (title === 'helpdesk') {
      window.open(environment.helpdeskUrl, '_blank');
    }
  }

  public downloadSeomStructureList(): void {
    const filename = 'SEOM Structure List.xlsx';
    this.httpClient
      .get(`${environment.documentDownloadCloudfront}${filename}`, { responseType: 'blob' })
      .pipe(
        this.loadingService.withLoadingScreen,
        this.httpHelpers.handleError('Failed to download SEOM Structure List'),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(file => {
        const blobStore = new Blob([file], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });
        const dataItem = window.URL.createObjectURL(blobStore);
        DownloadFileHelper.download(dataItem, filename);
      });
  }

  private async loadTranslations(): Promise<void> {
    this.languageSelected = await this.storageService.get(StorageKey.LANGUAGE);
    await this.loadHelpData();

    this.translateService.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(lang => {
      this.languageSelected = lang.lang as ContentLanguage;
      setTimeout(() => {
        this.loadHelpData();
      }, 600);
    });
  }

  private async loadHelpData(): Promise<void> {
    if (!this.helpData[this.languageSelected]) {
      this.isHelpDataLoading = true;
      const filterValue = 'cosmos';
      this.faqService
        .getFaq(`help_${this.languageSelected}_publish.json`, filterValue)
        .pipe(
          switchMap(data => iif(() => !data, this.faqService.getFaq(`help_en_publish.json`, filterValue), of(data))),
          filter(helpItem => !!helpItem),
          tap(helpItems => {
            this.searchForNewFlagItems(helpItems);
            this.helpData[this.languageSelected] = helpItems;
          }),
          finalize(() => {
            this.isHelpDataLoading = false;
            // a deep link can open the menu before the help data arrives
            this.cdr.markForCheck();
          })
        )
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe();
    }
  }

  private searchForNewFlagItems(helpItems: FaqItem[]): void {
    this.totalNewFlags = 0;
    for (const item of helpItems) {
      item.newFlagCount = this.checkItemsForFlag(item);
      this.totalNewFlags += item.newFlagCount;
    }
  }

  private checkItemsForFlag(item: FaqItem): number {
    if (item && item.content && !item.isAnswer) {
      let contentNewFlagCount = 0;
      if (item.newFlagUntil) {
        contentNewFlagCount += 1;
      } else {
        for (const itm of item.content) {
          itm.newFlagCount = this.checkItemsForFlag(itm);
          contentNewFlagCount += itm.newFlagCount;
        }
      }
      return contentNewFlagCount;
    }
    if (item && item.content && item.isAnswer) {
      if (item.newFlagUntil) {
        const timeUntilEndNewFlag =
          new Date(item.newFlagUntil).getTime() - new Date(new Date().setHours(0, 0, 0, 0)).getTime();
        if (timeUntilEndNewFlag > 0) {
          return 1;
        }
      }
    }
    return 0;
  }
}
